import dayjs from 'dayjs';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/dto.js';
import { TimeOfDay } from './time-of-day.js';

export class DayOfYear extends DataModel {
  static readonly TYPE_ID: string = '7e5f74d6fa6f4ff3b2a5fb128cefed42';

  public readonly uniqueId: string;

  constructor(
    public readonly year: number,
    public readonly month: number,
    public readonly day: number,
  ) {
    super();
    this.uniqueId = `${year}.${month.toString().padStart(2, '0')}.${day.toString().padStart(2, '0')}`;
  }

  get $typeId(): string {
    return DayOfYear.TYPE_ID;
  }

  static fromDate(date: dayjs.Dayjs): DayOfYear {
    return new DayOfYear(
        date.year(),
        date.month() + 1, // In dayjs months start at 0 not 1. The app expects month to start at 1.
        date.date(),
    );
  }

  toDate(timezone: TimeZone): dayjs.Dayjs {
    // In dayjs months start at 0 not 1. The app expects month to start at 1.
    return timezone.at(this.year, this.month - 1, this.day);
  }

  toDateAndTime(timeOfDay: TimeOfDay, timezone: TimeZone): dayjs.Dayjs {
    // In dayjs months start at 0 not 1. The app expects month to start at 1.
    return timezone.at(this.year, this.month - 1, this.day, timeOfDay.hour, timeOfDay.minute);
  }

  static fromMap(context: SerializationContext, map: SerializedData): DayOfYear {
    return new DayOfYear(
        MapUtils.get<number>(map, 'year'),
        MapUtils.get<number>(map, 'month'),
        MapUtils.get<number>(map, 'day'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'year': this.year,
      'month': this.month,
      'day': this.day,
    };
  }

  clone(): this {
    return new DayOfYear(
        this.year,
        this.month,
        this.day,
    ) as this;
  }

  isSame(other: DayOfYear): boolean {
    if(this === other) {
      return true;
    }

    return other.year === this.year && other.month === this.month && other.day === this.day;
  }

  static dateToUniqueId(date: dayjs.Dayjs): string {
    return `${date.year}.${date.month.toString().padStart(2, '0')}.${date.day.toString().padStart(2, '0')}`;
  }
}

export class DayOfYearBuilder extends DataModelBuilder<DayOfYear> {
  build(context: SerializationContext, map: SerializedData): DayOfYear {
    return DayOfYear.fromMap(context, map);
  }
}
